import { z } from 'zod';
import { dateStringSchema, monthEndAfter, parseDate } from '../dateString.js';
export const dateObservableSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('const'),
        value: dateStringSchema,
    }),
    z.object({ kind: z.literal('startOfMonth'), monthOffset: z.number() }),
    z.object({ kind: z.literal('endOfMonth'), monthOffset: z.number() }),
    z.object({
        kind: z.literal('dayOfMonth'),
        day: z.number().min(1).max(31),
        monthOffset: z.number(),
    }),
    z.object({
        kind: z.literal('addDays'),
        base: z.lazy(() => dateObservableSchema),
        days: z.number(),
    }),
    z.object({ kind: z.literal('context'), dateName: z.string().min(1) }),
]);
export const numericObservableSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('const'), value: z.number() }),
    z.object({ kind: z.literal('daysLate'), gracePeriod: z.number() }),
    z.object({ kind: z.literal('context'), factName: z.string().min(1) }),
    z.object({
        kind: z.literal('multiply'),
        left: z.lazy(() => numericObservableSchema),
        right: z.lazy(() => numericObservableSchema),
    }),
    z.object({
        kind: z.literal('max'),
        values: z.array(z.lazy(() => numericObservableSchema)).nonempty(),
    }),
    z.object({
        kind: z.literal('min'),
        values: z.array(z.lazy(() => numericObservableSchema)).nonempty(),
    }),
]);
export const conditionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('const'), value: z.boolean() }),
    z.object({ kind: z.literal('after'), date: dateObservableSchema }),
    z.object({ kind: z.literal('before'), date: dateObservableSchema }),
    z.object({ kind: z.literal('onOrAfter'), date: dateObservableSchema }),
    z.object({ kind: z.literal('onOrBefore'), date: dateObservableSchema }),
    z.object({ kind: z.literal('isLate'), gracePeriod: z.number() }),
    z.object({ kind: z.literal('and'), conditions: z.array(z.lazy(() => conditionSchema)) }),
    z.object({ kind: z.literal('or'), conditions: z.array(z.lazy(() => conditionSchema)) }),
    z.object({ kind: z.literal('not'), condition: z.lazy(() => conditionSchema) }),
    z.object({ kind: z.literal('context'), factName: z.string() }),
]);
export const recurrenceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('once') }),
    z.object({ kind: z.literal('daily'), interval: z.number().min(1) }),
    z.object({
        kind: z.literal('weekly'),
        dayOfWeek: z.number().min(0).max(6),
        interval: z.number().min(1),
    }),
    z.object({
        kind: z.literal('monthly'),
        dayOfMonth: z.number().min(1).max(31),
        interval: z.number().min(1),
    }),
    z.object({
        kind: z.literal('yearly'),
        month: z.number().min(1).max(12),
        day: z.number().min(1).max(31),
        interval: z.number().min(1),
    }),
    z.object({ kind: z.literal('monthEnd'), interval: z.number().min(1) }),
]);
export function recurrenceFor(cadence, interval, startDate) {
    const anchor = parseDate(startDate);
    switch (cadence) {
        case 'daily':
            return { kind: 'daily', interval };
        case 'weekly':
            return { kind: 'weekly', dayOfWeek: anchor.getDay(), interval };
        case 'monthly':
            return { kind: 'monthly', dayOfMonth: anchor.getDate(), interval };
        case 'yearly':
            return { kind: 'yearly', month: anchor.getMonth() + 1, day: anchor.getDate(), interval };
        case 'monthEnd':
            return { kind: 'monthEnd', interval };
    }
}
export function recurrenceForSuppliedStart(cadence, interval) {
    switch (cadence) {
        case 'daily':
            return { kind: 'daily', interval };
        case 'weekly':
            return { kind: 'weekly', dayOfWeek: 0, interval };
        case 'monthly':
            return { kind: 'monthly', dayOfMonth: 1, interval };
        case 'yearly':
            return { kind: 'yearly', month: 1, day: 1, interval };
        case 'monthEnd':
            return { kind: 'monthEnd', interval };
    }
}
export function firstOccurrence(recurrence, startDate) {
    return recurrence.kind === 'monthEnd' ? monthEndAfter(startDate, 0) : startDate;
}
export const taxonomyRefSchema = z.object({
    category: z.string().min(1).max(100),
    subcategory: z.string().min(1).max(200),
});
export const amountClassificationSchema = z.discriminatedUnion('kind', [
    taxonomyRefSchema.extend({ kind: z.literal('taxonomy') }),
    z.object({ kind: z.literal('chargeType'), chargeType: z.string().min(1).max(100) }),
]);
export function classificationLabel(classification) {
    return classification.kind === 'taxonomy'
        ? `${classification.category}: ${classification.subcategory}`
        : classification.chargeType;
}
export const amountSpecSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('unit') }),
    z.object({
        kind: z.literal('scale'),
        factor: numericObservableSchema,
        amount: z.lazy(() => amountSpecSchema),
    }),
    z.object({
        kind: z.literal('give'),
        amount: z.lazy(() => amountSpecSchema),
    }),
    z.object({
        kind: z.literal('when'),
        condition: conditionSchema,
        amount: z.lazy(() => amountSpecSchema),
    }),
    z.object({
        kind: z.literal('all'),
        amounts: z.array(z.lazy(() => amountSpecSchema)),
    }),
    z.object({
        kind: z.literal('first'),
        amounts: z.array(z.lazy(() => amountSpecSchema)),
    }),
    z.object({
        kind: z.literal('capped'),
        amount: z.lazy(() => amountSpecSchema),
        maxAmount: numericObservableSchema,
    }),
    z.object({
        kind: z.literal('describe'),
        description: z.string(),
        classification: amountClassificationSchema.optional(),
        amount: z.lazy(() => amountSpecSchema),
    }),
    z.object({
        kind: z.literal('surcharge'),
        rate: numericObservableSchema,
        description: z.string(),
        classification: amountClassificationSchema,
        amount: z.lazy(() => amountSpecSchema),
    }),
]);
export const capPeriodSchema = z.enum(['calendarYear', 'term']);
const fixedDateSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('const'), value: dateStringSchema }),
    z.object({ kind: z.literal('context'), dateName: z.string().min(1) }),
]);
export const dueDateSchema = z.discriminatedUnion('kind', [
    ...fixedDateSchema.options,
    z.object({ kind: z.literal('addDays'), base: fixedDateSchema, days: z.number() }),
]);
export const paymentSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('zero') }),
    z.object({
        kind: z.literal('once'),
        date: dueDateSchema,
        amount: amountSpecSchema,
        currency: z.string(),
    }),
    z.object({
        kind: z.literal('repeat'),
        recurrence: recurrenceSchema,
        amount: amountSpecSchema,
        currency: z.string(),
        startDate: dueDateSchema,
        endDate: dateStringSchema.optional(),
        maxOccurrences: z.number().optional(),
    }),
    z.object({
        kind: z.literal('all'),
        payments: z.array(z.lazy(() => paymentSchema)),
    }),
    z.object({
        kind: z.literal('first'),
        payments: z.array(z.lazy(() => paymentSchema)),
    }),
    z.object({
        kind: z.literal('notToExceed'),
        payment: z.lazy(() => paymentSchema),
        maxAmount: z.number().finite().nonnegative(),
        period: capPeriodSchema,
    }),
]);
//# sourceMappingURL=dsl.js.map